import React, { useState } from "react";
import { Truck, MapPin, Clock, Package, User, Navigation } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import RealTimeTrackDialog from "./realTimeTrack/RealTimeTrackDialog";
import { CommonSearchInput } from "@/components/ui/common-search-input";
import { TableBlurLoader } from "@/components/ui/loader";
import { Button } from "@/components/ui/button";

const VehicleCard = ({ vehicle, isSelected, onClick, onTrackTrip }) => {
  const latestUpdated = vehicle.trip?.latest_updated
    ? new Date(vehicle.trip.latest_updated).toLocaleString()
    : "N/A";

  const trip = vehicle.trip;
  const firstItem = trip?.items?.[0];
  const driverName = trip?.driver?.user?.username || trip?.driver?.name || "Unknown";
  const productName = trip?.product_type?.name || firstItem?.product_type?.name || "N/A";
  const productQty = trip?.product_quantity
    ? `${trip.product_quantity} ${trip.product_type?.unit_of_measure || ""}`
    : firstItem?.product_quantity
      ? `${firstItem.product_quantity} ${firstItem.product_type?.unit_of_measure || ""}`
      : "N/A";
  const consigneeName = trip?.consignee_name || firstItem?.consignee_name || "N/A";

  return (
    <motion.div
      onClick={onClick}
      layout
      initial={false}
      animate={{
        scale: isSelected ? 1.02 : 1,
        borderColor: isSelected
          ? "hsl(var(--primary))"
          : "hsl(var(--border))",
      }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
      className={`px-3 py-2.5 border rounded-xl cursor-pointer ${isSelected ? "z-10 bg-primary/5" : "bg-card"}`}
      style={{ position: "relative" }}
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex gap-3 items-center">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center border border-border shrink-0">
            <Truck size={18} className="text-primary" />
          </div>
          <div>
            <h4 className="text-[14px] font-medium leading-tight text-foreground">
              {vehicle.name}
            </h4>
            <p className="text-[12px] text-muted-foreground mt-0.5">
              Trip: {vehicle.trip?.trip_number || "N/A"}
            </p>
          </div>
        </div>
        <Badge
          variant="success"
          size="xs"
          className="py-0.5 px-2.5 text-[9px] font-medium uppercase tracking-wider shrink-0 mt-0.5"
        >
          {vehicle.status}
        </Badge>
      </div>

      <div className="h-px w-full mb-3" style={{ backgroundColor: "hsl(var(--border))" }}></div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <p className="text-[11px] text-muted-foreground mb-0.5 flex items-center gap-1">
            <User size={10} /> Driver
          </p>
          <p className="text-[13px] font-medium text-foreground truncate">
            {driverName}
          </p>
        </div>
        <div>
          <p className="text-[11px] text-muted-foreground mb-0.5 flex items-center gap-1">
            <Package size={10} /> Product
          </p>
          <p className="text-[13px] font-medium text-foreground truncate">
            {productName}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <p className="text-[11px] text-muted-foreground mb-0.5">Quantity</p>
          <p className="text-[13px] font-medium text-foreground">
            {productQty}
          </p>
        </div>
        <div>
          <p className="text-[11px] text-muted-foreground mb-0.5 flex items-center gap-1">
            <Clock size={10} /> Last Updated
          </p>
          <p className="text-[13px] font-medium text-foreground">{latestUpdated}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <p className="text-[11px] text-muted-foreground mb-0.5">Consignee</p>
          <p className="text-[13px] font-medium text-foreground truncate">
            {consigneeName}
          </p>
        </div>
        <div>
          <p className="text-[11px] text-muted-foreground mb-0.5 flex items-center gap-1">
            <MapPin size={10} /> Start Location
          </p>
          <p className="text-[13px] font-medium text-foreground">
            {vehicle.trip?.start_latitude
              ? `${vehicle.trip.start_latitude.toFixed(4)}, ${vehicle.trip.start_longitude.toFixed(4)}`
              : "N/A"}
          </p>
        </div>
      </div>

      <button
        onClick={(e) => {
          e.stopPropagation();
          onTrackTrip(vehicle.trip);
        }}
        className="flex items-center justify-center gap-2 w-full h-9 text-[13px] font-medium transition-colors cursor-pointer rounded-lg border border-border bg-card text-foreground hover:bg-muted/50"
      >
        <Navigation size={13} />
        Track Trip
      </button>
    </motion.div>
  );
};

const VehicleList = ({
  vehicles,
  selectedVehicle,
  onSelectVehicle,
  searchTerm,
  setSearchTerm,
  loading,
  currentPage,
  onPageChange,
  totalItems,
  pageSize
}) => {
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const paginatedVehicles = vehicles;

  return (
    <>
      <div className="w-full h-full flex flex-col">
        <div className="px-4 pt-4 pb-0">
          {/* <h2 className="text-lg font-semibold text-foreground tracking-tight mb-4">
            Live Tracking
          </h2> */}
          <div className="mb-4">
            <CommonSearchInput
              placeholder="Search by trip or vehicle..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onSearch={(value) => setSearchTerm(value)}
            />
          </div>
        </div>

        <div className="flex-1 px-4 pt-1 pb-4 overflow-y-auto" style={{ scrollbarWidth: "none" }}>
          <TableBlurLoader isLoading={loading} className="h-full">
            <div className="flex flex-col gap-3">
              {paginatedVehicles.length > 0 ? (
                paginatedVehicles.map((vehicle) => (
                  <VehicleCard
                    key={vehicle.id}
                    vehicle={vehicle}
                    isSelected={selectedVehicle?.id === vehicle.id}
                    onClick={() => onSelectVehicle && onSelectVehicle(vehicle)}
                    onTrackTrip={(trip) => {
                      setSelectedTrip(trip);
                      setIsDialogOpen(true);
                    }}
                  />
                ))
              ) : (
                <div className="text-center text-sm py-10 text-muted-foreground opacity-50">
                  No active trips found
                </div>
              )}
            </div>
          </TableBlurLoader>
        </div>

        {!loading && totalItems > 0 && (
          <div className="px-4 py-3 border-t border-border mt-auto flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> -{" "}
              <span className="font-medium">{Math.min(currentPage * pageSize, totalItems)}</span> of{" "}
              <span className="font-medium">{totalItems}</span>
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="h-7 px-2 text-xs"
              >
                Previous
              </Button>
              <span className="text-xs text-muted-foreground px-2">
                {currentPage} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="h-7 px-2 text-xs"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {selectedTrip && (
        <RealTimeTrackDialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          trip={selectedTrip}
        />
      )}
    </>
  );
};

export default VehicleList;